import { useCallback, useEffect, useRef, useState } from 'react';

import { styled } from '@mui/material';
import { NotificationBadge, Tooltip } from '@kc-console/common-ui';
import {
  useMarkNotificationsSeen,
  useNotificationBadge,
  useNotificationWithSSE,
  useReadNotification,
  useStreamControl,
} from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { IcNotificationOnRegular } from '@kep/kcds-icons';

import { NotificationPopper } from '../../../Notification';
import GnbIconButton from '../GnbIconButton';

import type { Notification } from '@kc-console/context';

const MAX_BADGE_COUNT = 99;
const NOTIFICATION_TITLE_PREFIX_PATTERN = /^\(\d+\+?\)\s+/;

const getNotificationTitleCount = (count: number) =>
  count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(count);

const removeNotificationTitlePrefix = (title: string) =>
  title.replace(NOTIFICATION_TITLE_PREFIX_PATTERN, '');

/**
 * GNB 우측 종 모양 알림 메뉴.
 * @description
 * - useNotificationBadge 의 unseenCount 가 0 보다 크면 우상단에 빨간 배지 노출.
 * - 클릭 시 NotificationPopper 토글 + 열릴 때 lastSeenAt cursor 갱신(POST /notifications/seen).
 */
const NotificationMenu = () => {
  const { t } = useTranslation();
  const defaultTooltipMessage = t('layout:modal.title.notification');
  const streamDisconnectedTooltip = t('layout:notification.bell.disconnected.message');

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const { data: badge } = useNotificationBadge();
  const { mutate: markSeen } = useMarkNotificationsSeen();
  const { mutate: read } = useReadNotification();
  const { isConnectionLost } = useStreamControl();
  const unseenCount = badge?.unseenCount ?? 0;

  // SSE 끊김이 최우선. 끊긴 상태면 경고(status) 배지, 아니면 안 본 개수(count) 배지를 노출한다.
  const renderBellBadge = () => {
    if (isConnectionLost) {
      return <NotificationBadge variant="status" />;
    }
    if (unseenCount > 0) {
      return <NotificationBadge count={unseenCount} max={MAX_BADGE_COUNT} />;
    }
    return null;
  };
  const bellBadge = renderBellBadge();

  const openNotificationPopper = useCallback(() => {
    setOpen(true);
    markSeen();
  }, [markSeen]);

  const handleNotificationToastClick = useCallback(
    (notification: Notification) => {
      openNotificationPopper();

      if (!notification.read && notification.id) {
        read(notification.id);
      }
    },
    [openNotificationPopper, read],
  );

  useNotificationWithSSE({ onNotificationToastClick: handleNotificationToastClick });

  useEffect(() => {
    const baseTitle = removeNotificationTitlePrefix(document.title);

    document.title =
      unseenCount > 0 ? `(${getNotificationTitleCount(unseenCount)}) ${baseTitle}` : baseTitle;

    return () => {
      document.title = removeNotificationTitlePrefix(document.title);
    };
  }, [unseenCount]);

  const handleToggle = useCallback(() => {
    setOpen(prev => {
      const next = !prev;
      if (next && unseenCount > 0) {
        markSeen();
      }
      return next;
    });
  }, [markSeen, unseenCount]);

  const handleCloseNotification = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <Tooltip message={isConnectionLost ? streamDisconnectedTooltip : defaultTooltipMessage}>
        <div ref={anchorRef}>
          <GnbIconButton disableRipple onClick={handleToggle}>
            <BellWrap>
              <IcNotificationOnRegular />
              {bellBadge && (
                <BadgeOverlay isConnectionLost={isConnectionLost}>{bellBadge}</BadgeOverlay>
              )}
            </BellWrap>
          </GnbIconButton>
        </div>
      </Tooltip>
      <NotificationPopper
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleCloseNotification}
        placement="bottom-end"
      />
    </>
  );
};

export default NotificationMenu;

const BellWrap = styled('span')`
  position: relative;
  display: inline-flex;
`;

/**
 * 종 아이콘 우상단에 NotificationBadge를 겹쳐 놓는 오버레이.
 * @description
 * - 우상단 모서리에 앵커한 뒤 살짝 바깥으로 튀어나오게 배치한다.
 * - pointer-events를 꺼서 배지 클릭도 종 버튼으로 전달되게 한다.
 * - GnbIconButton의 `svg { width:20 }` 규칙이 status(경고) 아이콘까지 20px로
 *   키우므로 `&&`로 우선순위를 높여 12px로 되돌린다(count 배지는 span이라 무관).
 * - status(끊김)와 count 배지의 크기가 달라 세로 위치를 각각 보정한다.
 */
const BadgeOverlay = styled('span', {
  shouldForwardProp: prop => prop !== 'isConnectionLost',
})<{ isConnectionLost?: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(-15%, ${({ isConnectionLost }) => (isConnectionLost ? '-50%' : '-60%')});
  pointer-events: none;

  && svg {
    width: 12px;
    height: 12px;
  }
`;
